import { useState } from "react";
import {
  Card,
  CardContent,
  Button,
  Typography,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Box,
  InputAdornment,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../components/config/config";

function ForgotPassword() {
  const navigate = useNavigate();

  // State management
  const [step, setStep] = useState(1); // 1: Enter mobile, 2: Enter OTP and new password
  const [mobileNumber, setMobileNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const loginPage = () => navigate("/admin-login");

  const validateMobileNumber = () => {
    const mobileNumberRegex = /^[0-9]{10}$/;
    if (!mobileNumberRegex.test(mobileNumber)) {
      setErrorMessage("Mobile number must be 10 digits");
      return false;
    }
    setErrorMessage("");
    return true;
  };

  const sendOtp = () => {
    if (!validateMobileNumber()) return;

    setIsSendingOtp(true);
    fetch(`${BASE_URL}admin/sent-otp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        country_code: countryCode,
        mobile_number: mobileNumber,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        setIsSendingOtp(false);
        if (data.success) {
          setSnackbar({
            open: true,
            message: "OTP sent successfully!",
            severity: "success",
          });
          setStep(2);
        } else {
          setSnackbar({
            open: true,
            message: data.message || "Failed to send OTP",
            severity: "error",
          });
        }
      })
      .catch((error) => {
        setIsSendingOtp(false);
        console.error("Error:", error);
        setSnackbar({
          open: true,
          message: "An error occurred. Please try again.",
          severity: "error",
        });
      });
  };

  const resetPassword = () => {
    // Validate inputs
    if (!otp || !newPassword || !confirmPassword) {
      setErrorMessage("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setErrorMessage("Password must be at least 8 characters");
      return;
    }

    setErrorMessage("");
    setIsLoading(true);

    fetch(`${BASE_URL}admin/forgot-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        country_code: countryCode,
        mobile_number: mobileNumber,
        otp: parseInt(otp),
        newPassword: newPassword,
        confirmPassword: confirmPassword,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        setIsLoading(false);
        if (data.success) {
          setSnackbar({
            open: true,
            message: "Password reset successfully!",
            severity: "success",
          });
          setTimeout(() => navigate("/admin-login"), 2000);
        } else {
          setSnackbar({
            open: true,
            message: data.message || "Failed to reset password",
            severity: "error",
          });
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Error:", error);
        setSnackbar({
          open: true,
          message: "An error occurred. Please try again.",
          severity: "error",
        });
      });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
        p: 2,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 450,
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #00897b 0%, #00695c 100%)",
            color: "white",
            p: 3,
            textAlign: "center",
          }}
        >
          <LockIcon sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h5" component="h1" fontWeight="bold">
            {step === 1 ? "Forgot Password" : "Reset Password"}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
            {step === 1
              ? "Enter your mobile number to receive a verification code"
              : "Enter the OTP and set your new password"}
          </Typography>
        </Box>

        <CardContent sx={{ p: 3 }}>
          {step === 1 ? (
            <>
              <TextField
                fullWidth
                label="Mobile Number"
                variant="outlined"
                value={mobileNumber}
                onChange={(e) =>
                  setMobileNumber(
                    e.target.value.replace(/\D/g, "").slice(0, 10)
                  )
                }
                disabled={isSendingOtp}
                inputProps={{ maxLength: 10 }}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              {errorMessage && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {errorMessage}
                </Typography>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={sendOtp}
                disabled={isSendingOtp || !mobileNumber}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, #00897b 0%, #00695c 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #00796b 0%, #004d40 100%)",
                  },
                }}
              >
                {isSendingOtp ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Send Verification Code"
                )}
              </Button>
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                  p: 2,
                  backgroundColor: "#e0f2f1",
                  borderRadius: 2,
                }}
              >
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  OTP sent to {countryCode} {mobileNumber}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Verification Code"
                variant="outlined"
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                inputProps={{ maxLength: 6 }}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                variant="outlined"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowConfirmPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {errorMessage && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {errorMessage}
                </Typography>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={resetPassword}
                disabled={isLoading || !otp || !newPassword || !confirmPassword}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, #00897b 0%, #00695c 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #00796b 0%, #004d40 100%)",
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Reset Password"
                )}
              </Button>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: "center" }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={loginPage}
              sx={{
                color: "#00897b",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#00695c",
                },
              }}
            >
              Back to Login
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ForgotPassword;
